import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { LandingPage } from './components/landing/LandingPage';
import { FeaturesPage } from './components/pages/FeaturesPage';
import { BenefitsPage } from './components/pages/BenefitsPage';
import { PricingPage } from './components/pages/PricingPage';
import { ContactPage } from './components/pages/ContactPage';
import { ChatWidget } from './components/chat/ChatWidget';
import { DebugButton } from './components/debug/DebugButton';
import { DebugPanel } from './components/debug/DebugPanel';
import { useDebugStore } from './store/debugStore';
import { useAuthStore } from './store/authStore'; // Import useAuthStore
import { UserDashboard } from './components/user/UserDashboard'; // Import UserDashboard
import { AdminDashboard } from './components/admin/AdminDashboard'; // Import AdminDashboard

// ProtectedRoute Component Definition
interface ProtectedRouteProps {
  allowedRoles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ allowedRoles }) => {
  const { user } = useAuthStore();
  const location = useLocation();

  if (!user) {
    // User not logged in, redirect to home page
    // Storing the intended location to redirect back after login
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // User is logged in but does not have the required role
    // Redirect to their default dashboard or an unauthorized page
    if (user.role === 'admin') {
        return <Navigate to="/admin/dashboard" replace />;
    } else if (user.role === 'user') {
        return <Navigate to="/dashboard" replace />;
    }
    // Fallback if role is unrecognized, or no specific dashboard for role
    return <Navigate to="/" replace />; 
  }

  return <Outlet />; // User is authenticated and has the role
};


function App() {
  const { isVisible, logs, clearLogs, setIsVisible } = useDebugStore();
  // const { user } = useAuthStore(); // authStore can be used directly in ProtectedRoute

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/functies" element={<FeaturesPage />} />
          <Route path="/voordelen" element={<BenefitsPage />} />
          <Route path="/prijzen" element={<PricingPage />} />
          <Route path="/contact" element={<ContactPage />} />

          {/* Protected User Dashboard Route */}
          <Route element={<ProtectedRoute allowedRoles={['user', 'admin']} />}>
            <Route path="/dashboard" element={<UserDashboard />} />
          </Route>

          {/* Protected Admin Dashboard Route */}
          <Route element={<ProtectedRoute allowedRoles={['admin']} />}>
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
          </Route>

        </Routes>
        
        <ChatWidget />
        <DebugButton />
        {isVisible && (
          <DebugPanel 
            logs={logs} 
            onClear={clearLogs} 
            onClose={() => setIsVisible(false)} 
          />
        )}
      </div>
    </Router>
  );
}

export default App;